import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Search, Loader2, AlertCircle, ChevronLeft, ChevronRight, ArrowUpDown, ShoppingCart, Filter, RotateCcw, FileSpreadsheet,
} from "lucide-react";

const PAGE_SIZE = 100;

const fmtCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.replace("T", " ").slice(0, 19);
  return s;
};

export default function Compras() {
  const { activeCompanyId } = useAuth();
  const storeKey = `compras_filters_${activeCompanyId}`;

  const defaults = { fecha_inicial: today(), fecha_final: today(), search: "" };
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(storeKey);
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch { return defaults; }
  });
  const [data, setData] = useState(null); // {columns, rows}
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [executed, setExecuted] = useState(false);
  const [sort, setSort] = useState({ col: null, order: "asc" });
  const [page, setPage] = useState(1);

  useEffect(() => {
    try { localStorage.setItem(storeKey, JSON.stringify(filters)); }
    catch (e) { console.warn("No se pudieron guardar los filtros de compras:", e); }
  }, [filters, storeKey]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const consultar = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const { fecha_inicial, fecha_final } = filters;
      const res = await api.get(`/compras/externas?fecha_inicial=${fecha_inicial}&fecha_final=${fecha_final}`);
      setData({ columns: res.data.columns || [], rows: res.data.items || [] });
      setExecuted(true);
      setPage(1);
      setSort({ col: null, order: "asc" });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "No se pudo consultar la información";
      setError(msg);
      setData(null);
      setExecuted(true);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, loading]);

  const exportarExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { fecha_inicial, fecha_final, search } = filters;
      const res = await api.get(
        `/compras/externas/export?fecha_inicial=${fecha_inicial}&fecha_final=${fecha_final}&search=${encodeURIComponent(search || "")}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compras_${fecha_inicial}_a_${fecha_final}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Compras exportadas a Excel");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo exportar");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => setFilters(defaults);

  const columns = data?.columns || [];

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.rows;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      rows = rows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(q)));
    }
    if (sort.col) {
      rows = [...rows].sort((a, b) => {
        const av = a[sort.col], bv = b[sort.col];
        const an = parseFloat(av), bn = parseFloat(bv);
        let cmp;
        if (!isNaN(an) && !isNaN(bn) && String(av).trim() !== "" && String(bv).trim() !== "") cmp = an - bn;
        else cmp = String(av ?? "").localeCompare(String(bv ?? ""), "es", { numeric: true });
        return sort.order === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }, [data, filters.search, sort]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (col) => {
    setSort((s) => (s.col === col ? { col, order: s.order === "asc" ? "desc" : "asc" } : { col, order: "asc" }));
    setPage(1);
  };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <h3 className="font-heading font-semibold text-slate-900">Consulta de Compras Externas</h3>
          <span className="ml-auto text-xs text-slate-400">Solo lectura</span>
        </div>
        <div className="p-6 flex flex-wrap items-end gap-4">
          <div className="w-44">
            <label className="text-xs text-slate-500">Fecha Inicial *</label>
            <input data-testid="compras-fecha-inicial" type="date" className={input} value={filters.fecha_inicial} onChange={(e) => set("fecha_inicial", e.target.value)} />
          </div>
          <div className="w-44">
            <label className="text-xs text-slate-500">Fecha Final *</label>
            <input data-testid="compras-fecha-final" type="date" className={input} value={filters.fecha_final} onChange={(e) => set("fecha_final", e.target.value)} />
          </div>
          <button data-testid="compras-consultar" onClick={consultar} disabled={loading}
            className="bg-slate-900 text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Consultar
          </button>
          <button data-testid="compras-reset" onClick={resetFilters} disabled={loading} title="Limpiar filtros"
            className="rounded-md border border-slate-300 px-3 py-2 hover:bg-slate-50 disabled:opacity-60"><RotateCcw className="h-4 w-4 text-slate-500" /></button>
          {data && total > 0 && (
            <button data-testid="compras-export" onClick={exportarExcel} disabled={exporting}
              className="ml-auto rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />} Exportar Excel
            </button>
          )}
        </div>
      </div>

      {/* Buscador global + total */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative max-w-xs w-full">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="compras-search" value={filters.search} onChange={(e) => { set("search", e.target.value); setPage(1); }}
            placeholder="Buscar en resultados…" className="w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        {data && (
          <span className="text-sm text-slate-500" data-testid="compras-total">
            <span className="font-bold text-slate-900 tabular-nums">{pageRows.length}</span> / <span className="font-bold text-slate-900 tabular-nums">{total}</span> registros encontrados
          </span>
        )}
      </div>

      {/* Estados */}
      {loading && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-16 text-center" data-testid="compras-loading">
          <Loader2 className="h-8 w-8 text-slate-400 animate-spin mx-auto" />
          <p className="mt-3 text-slate-600 font-medium">Consultando información...</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-white border border-red-200 rounded-md shadow-sm p-12 text-center" data-testid="compras-error">
          <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mx-auto"><AlertCircle className="h-6 w-6 text-red-500" /></div>
          <p className="mt-3 text-slate-900 font-medium">{error}</p>
          <p className="text-sm text-slate-500 mt-1">Ajusta los filtros e inténtalo nuevamente.</p>
        </div>
      )}

      {!loading && !error && !executed && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-16 text-center" data-testid="compras-initial">
          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto"><ShoppingCart className="h-6 w-6 text-slate-400" /></div>
          <p className="mt-3 text-slate-600">Selecciona el rango de fechas y presiona <span className="font-medium text-slate-900">Consultar</span> para ver las compras.</p>
        </div>
      )}

      {!loading && !error && executed && data && total === 0 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-16 text-center" data-testid="compras-empty">
          <p className="text-slate-500">No se encontraron resultados con los filtros seleccionados.</p>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && data && total > 0 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
                <tr>
                  {columns.map((c) => (
                    <th key={c} className="px-4 py-3 font-medium text-left whitespace-nowrap">
                      <button onClick={() => toggleSort(c)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`compras-sort-${c}`}>
                        {c} <ArrowUpDown className={`h-3 w-3 ${sort.col === c ? "text-slate-900" : "text-slate-300"}`} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="compras-table">
                {pageRows.map((r, i) => (
                  <tr key={r["#COMPRA"] ?? r.id ?? i} className="border-b border-slate-100 hover:bg-slate-50/50">
                    {columns.map((c) => (
                      <td key={c} className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{fmtCell(r[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
            <span>Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}</span>
            <div className="flex items-center gap-2">
              <button data-testid="compras-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
              <select data-testid="compras-page-select" value={page} onChange={(e) => setPage(Number(e.target.value))} className="rounded-md border border-slate-300 px-2 py-1 text-sm">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => <option key={p} value={p}>Página {p}</option>)}
              </select>
              <span>de {totalPages}</span>
              <button data-testid="compras-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
